import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("bnm_admin_token")}` });

const STATUS_CHIP = {
  new: "bg-volt/15 text-volt border-volt/30",
  in_progress: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  done: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  closed: "bg-slate-500/10 text-slate-500 border-slate-600/30",
};

const inputCls = "h-10 w-full rounded-md border border-white/15 bg-obsidian/60 px-3 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt";

function Card({ title, children, testid }) {
  return (
    <div className="rounded-lg border border-white/10 bg-pine/60 p-6" data-testid={testid}>
      <h3 className="font-display text-base font-bold text-white">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function StatusButtons({ item, collection, onDone }) {
  const set = async (status) => {
    try {
      await axios.patch(`${API}/admin/energy/${collection}/${item.id}`, { status }, { headers: adminHeaders() });
      toast.success(`Marked ${status.replace("_", " ")}`);
      onDone();
    } catch { toast.error("Update failed"); }
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${STATUS_CHIP[item.status] || STATUS_CHIP.new}`}>{item.status}</span>
      {item.status !== "in_progress" && <button onClick={() => set("in_progress")} className="rounded-md border border-white/15 px-2.5 py-1 text-[11px] text-slate-300 hover:text-amber-400 hover:border-amber-400/40">In progress</button>}
      {item.status !== "done" && <button onClick={() => set("done")} data-testid={`admin-energy-done-${item.id}`} className="rounded-md border border-white/15 px-2.5 py-1 text-[11px] text-slate-300 hover:text-volt hover:border-volt/40">Done</button>}
    </div>
  );
}

export default function AdminEnergy() {
  const [data, setData] = useState(null);
  const [locForm, setLocForm] = useState({ name: "", district: "Bo", type: "community_request", note: "" });
  const [artForm, setArtForm] = useState({ title: "", slug: "", category: "General", summary: "", body: "" });

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/energy/overview`, { headers: adminHeaders() });
    setData(data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  if (!data) return <p className="py-10 text-sm text-slate-500">Loading…</p>;
  const a = data.analytics;

  const addLocation = async () => {
    if (!locForm.name.trim()) return;
    try {
      await axios.post(`${API}/admin/energy/locations`, { ...locForm, public: true }, { headers: adminHeaders() });
      toast.success("Location added");
      setLocForm({ name: "", district: "Bo", type: "community_request", note: "" });
      load();
    } catch { toast.error("Add failed"); }
  };

  const deleteLocation = async (id) => {
    await axios.delete(`${API}/admin/energy/locations/${id}`, { headers: adminHeaders() });
    load();
  };

  const addArticle = async () => {
    if (!artForm.title.trim() || !artForm.slug.trim() || artForm.body.trim().length < 10) {
      toast.error("Title, slug and body are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/energy/articles`, artForm, { headers: adminHeaders() });
      toast.success("Article published");
      setArtForm({ title: "", slug: "", category: "General", summary: "", body: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    }
  };

  const deleteArticle = async (slug) => {
    if (!window.confirm("Delete this article?")) return;
    await axios.delete(`${API}/admin/energy/articles/${slug}`, { headers: adminHeaders() });
    toast.success("Article deleted");
  };

  const stat = (label, value) => (
    <div className="rounded-md border border-white/10 bg-obsidian/40 p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="mt-1.5 font-display text-xl font-extrabold text-white">{value}</p>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="energy-analytics">
        {stat("Energy plans", a.total_plans)}
        {stat("Leads", a.total_leads)}
        {stat("Support requests", a.total_support)}
        {stat("Community requests", a.total_community)}
        {stat("Paid assessments", a.total_assessments)}
        {stat("Lead types", Object.keys(a.lead_types).length)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card title="Most common appliances" testid="analytics-appliances">
          {a.top_appliances.length ? a.top_appliances.map(([n, c]) => <p key={n} className="text-xs text-slate-300 py-1">{n} — {c}</p>) : <p className="text-xs text-slate-500">No data yet</p>}
        </Card>
        <Card title="Most common property types" testid="analytics-types">
          {a.top_property_types.length ? a.top_property_types.map(([n, c]) => <p key={n} className="text-xs text-slate-300 py-1 capitalize">{n.replace(/_/g, " ")} — {c}</p>) : <p className="text-xs text-slate-500">No data yet</p>}
        </Card>
        <Card title="Most requested locations" testid="analytics-locations">
          {a.top_locations.length ? a.top_locations.map(([n, c]) => <p key={n} className="text-xs text-slate-300 py-1">{n} — {c}</p>) : <p className="text-xs text-slate-500">No data yet</p>}
        </Card>
      </div>

      <Card title={`Leads & Requests (${data.leads.length})`} testid="energy-leads">
        {data.leads.length === 0 && <p className="text-xs text-slate-500">No leads yet.</p>}
        <div className="space-y-3">
          {data.leads.map((l) => (
            <div key={l.id} data-testid={`admin-lead-${l.id}`} className="rounded-md border border-white/10 bg-obsidian/40 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-white">{l.name} <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-volt/70 ml-2">{l.type}</span></p>
                <StatusButtons item={l} collection="leads" onDone={load} />
              </div>
              <p className="mt-1 text-xs text-slate-400">{l.phone || "—"}{l.whatsapp ? ` · WA: ${l.whatsapp}` : ""}{l.email ? ` · ${l.email}` : ""}{l.location ? ` · ${l.location}` : ""}</p>
              {l.message && <p className="mt-2 text-xs text-slate-300 whitespace-pre-wrap">{l.message}</p>}
              {l.context && <p className="mt-2 font-mono text-[10px] text-slate-500">{Object.entries(l.context).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>}
              <p className="mt-1.5 font-mono text-[10px] text-slate-600">{new Date(l.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card title={`Technical Support Requests (${data.support.length})`} testid="energy-support">
        {data.support.length === 0 && <p className="text-xs text-slate-500">No support requests yet.</p>}
        <div className="space-y-3">
          {data.support.map((s) => (
            <div key={s.id} data-testid={`admin-support-${s.id}`} className="rounded-md border border-white/10 bg-obsidian/40 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-white">{s.name} · {s.phone}</p>
                <StatusButtons item={s} collection="support" onDone={load} />
              </div>
              <p className="mt-1 text-xs text-slate-400">{s.location || "—"} · {s.system_type || "unknown system"}</p>
              <p className="mt-2 text-xs text-slate-300 whitespace-pre-wrap">{s.description}</p>
              {Object.entries(s.files || {}).map(([kind, fname]) => (
                <a key={kind} href={`${API}/admin/energy/files/${fname}`}
                  onClick={async (e) => {
                    e.preventDefault();
                    const res = await axios.get(`${API}/admin/energy/files/${fname}`, { headers: adminHeaders(), responseType: "blob" });
                    window.open(URL.createObjectURL(res.data), "_blank");
                  }}
                  className="mt-2 mr-3 inline-block text-xs text-volt hover:underline">View {kind}</a>
              ))}
            </div>
          ))}
        </div>
      </Card>

      <Card title={`Community Energy Requests (${data.community.length})`} testid="energy-community">
        {data.community.length === 0 && <p className="text-xs text-slate-500">No community requests yet.</p>}
        <div className="space-y-3">
          {data.community.map((c) => (
            <div key={c.id} data-testid={`admin-community-${c.id}`} className="rounded-md border border-white/10 bg-obsidian/40 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-white">{c.community}, {c.district}</p>
                <StatusButtons item={c} collection="community" onDone={load} />
              </div>
              <p className="mt-1 text-xs text-slate-400">Households: {c.households || "—"} · Current source: {c.current_source || "—"}</p>
              {c.challenges && <p className="mt-2 text-xs text-slate-300">{c.challenges}</p>}
            </div>
          ))}
        </div>
      </Card>

      <Card title={`Paid Energy Assessments (${data.assessments.length})`} testid="energy-assessments">
        {data.assessments.length === 0 && <p className="text-xs text-slate-500">No paid assessments yet.</p>}
        <div className="space-y-2">
          {data.assessments.map((o) => (
            <div key={o.reference} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white/10 bg-obsidian/40 px-4 py-3">
              <p className="font-mono text-xs text-white">{o.reference} · SLE {o.amount}</p>
              <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase ${STATUS_CHIP[o.payment_status] || STATUS_CHIP.new}`}>{o.payment_status}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-500">Mark assessment payments paid/refunded in the Solar Orders tab.</p>
      </Card>

      <Card title={`Map Locations (${data.locations.length})`} testid="energy-locations">
        <div className="space-y-2">
          {data.locations.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-obsidian/40 px-4 py-3">
              <p className="text-xs text-slate-300">{l.name} · {l.district} · <span className="text-volt/70">{l.type.replace(/_/g, " ")}</span></p>
              <button onClick={() => deleteLocation(l.id)} data-testid={`admin-loc-del-${l.id}`} className="text-slate-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2 items-end border-t border-white/10 pt-4">
          <input placeholder="Name" className={inputCls} data-testid="loc-name" value={locForm.name} onChange={(e) => setLocForm((f) => ({ ...f, name: e.target.value }))} />
          <select className={inputCls} data-testid="loc-district" value={locForm.district} onChange={(e) => setLocForm((f) => ({ ...f, district: e.target.value }))}>
            {["Western Area Urban (Freetown)", "Western Area Rural (Waterloo)", "Bo", "Kenema", "Makeni (Bombali)", "Port Loko", "Kambia", "Moyamba", "Bonthe", "Pujehun", "Kailahun", "Kono", "Tonkolili", "Koinadugu", "Falaba", "Karene"].map((d) => <option key={d}>{d}</option>)}
          </select>
          <select className={inputCls} data-testid="loc-type" value={locForm.type} onChange={(e) => setLocForm((f) => ({ ...f, type: e.target.value }))}>
            {Object.keys({ bnm_project: 1, solar_installation: 1, cfg_deployment: 1, community_request: 1, school_request: 1, clinic_request: 1, business_request: 1, minigrid_opportunity: 1 }).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <input placeholder="Note (optional)" className={inputCls} value={locForm.note} onChange={(e) => setLocForm((f) => ({ ...f, note: e.target.value }))} />
          <button onClick={addLocation} data-testid="loc-add" className="inline-flex items-center justify-center gap-1.5 rounded-md border border-volt/40 px-3 py-2.5 text-xs font-semibold text-volt hover:bg-volt/10">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </Card>

      <Card title="Knowledge Center — New Article" testid="energy-articles">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input placeholder="Title" className={inputCls} data-testid="art-title" value={artForm.title} onChange={(e) => setArtForm((f) => ({ ...f, title: e.target.value }))} />
          <input placeholder="slug-like-this" className={inputCls} data-testid="art-slug" value={artForm.slug} onChange={(e) => setArtForm((f) => ({ ...f, slug: e.target.value }))} />
          <input placeholder="Category" className={inputCls} value={artForm.category} onChange={(e) => setArtForm((f) => ({ ...f, category: e.target.value }))} />
        </div>
        <input placeholder="Summary" className={`${inputCls} mt-2`} value={artForm.summary} onChange={(e) => setArtForm((f) => ({ ...f, summary: e.target.value }))} />
        <textarea placeholder="Article body" rows={4} className="mt-2 w-full rounded-md border border-white/15 bg-obsidian/60 px-3 py-2.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
          data-testid="art-body" value={artForm.body} onChange={(e) => setArtForm((f) => ({ ...f, body: e.target.value }))} />
        <button onClick={addArticle} data-testid="art-add" className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-volt/40 px-4 py-2.5 text-xs font-semibold text-volt hover:bg-volt/10">
          <Plus className="h-3.5 w-3.5" /> Publish Article
        </button>
        <p className="mt-3 text-[11px] text-slate-500">To remove an article, use the API: DELETE /api/admin/energy/articles/{"{slug}"}.</p>
      </Card>
    </div>
  );
}
